import {BUDGET_TYPE, QUANTITY_LOCKED_REASON} from '../../../../constants';
import {convertPriceToInvoiceCurrency, addQuantityDecimals} from './invoicing_generate_lines_utils';

/**
 * What is left to bill on a Fixed Price Deliverable, in the project's currency: its fee less
 * everything already invoiced against it. Zero for an unpriced deliverable.
 *
 * Clamped at zero because a fee can be lowered below what has already been invoiced, and a negative
 * remainder must never reach the invoicing UI as a billable amount.
 *
 * Rounded to two decimals because both operands arrive from DECIMAL(11,2) columns as floats, so the
 * subtraction leaves binary residue — 1000 - 333.33 is 666.6700000000001. Rounding recovers the
 * exact remainder rather than approximating it, and without it that residue reaches both the amount
 * sent to the server, which refuses more than two decimals, and the amount shown on screen.
 */
export const remainingToInvoice = deliverable =>
	deliverable.price == null
		? 0
		: Math.max(0, Math.round((deliverable.price - (deliverable.invoicedAmount ?? 0)) * 100) / 100);

// Fixed Fee Deliverable invoicing is scoped to Time & Materials projects only (per the "Deliverables
// with Different Billing Structures" PRD's V1 scope) — a Deliverable's price field isn't itself
// restricted by project budgetType, so this must be checked explicitly rather than assumed.
export const isFixedPriceDeliverableInvoiceable = (deliverable, project) =>
	project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS && deliverable.price != null && remainingToInvoice(deliverable) > 0;

/**
 * Builds the invoice line for billing a Fixed Price Deliverable, defaulting to everything that
 * remains on it.
 *
 * The line carries the amount twice, in two currencies, because they serve different consumers:
 * `unitPrice` and its ceiling `maxUnitPrice` are in invoice currency and are what the entries grid
 * shows and edits, while `maxAmount` and `deliverableAmounts[].amount` are in project currency and
 * are what the deliverable's ledger records. `maxAmount` also lets a line whose price has not been
 * touched send the exact remaining amount rather than a figure converted twice.
 */
export const buildLineForFixedPriceDeliverable = (deliverable, project, invoice, company) => {
	const projectCurrency = project.rateCard ? project.rateCard.currency : company.currency;
	const amount = remainingToInvoice(deliverable);
	const unitPrice = convertPriceToInvoiceCurrency(amount, projectCurrency, company, invoice);

	return {
		description: deliverable.name,
		quantity: addQuantityDecimals(1, company),
		unitPrice,
		maxUnitPrice: unitPrice,
		maxAmount: amount,
		projectId: project.id,
		deliverableIds: [deliverable.id],
		deliverableAmounts: [{deliverableId: deliverable.id, amount}],
		tax: invoice.tax,
		discount: invoice.discount,
		quantityLockedReason: QUANTITY_LOCKED_REASON.FIXED_PRICE_DELIVERABLE,
	};
};
